<style>
.menu-item {
  padding: 0;
  color: var(--text-color) !important;
}
.menu-item:hover {
  color: var(--hover-color) !important;
}

.sidebar {
  transition: all 0.3s ease;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  max-height: calc(100vh - 65px);
}

.sidebar::-webkit-scrollbar {
  width: 5px;
}
.sidebar::-webkit-scrollbar-track {
  background: transparent;
}
.sidebar::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.18);
  border-radius: 4px;
}
.sidebar::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.35);
}

.sidebar-expanded {
  width: 224px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1.25rem 0.75rem 4rem 0.75rem;
}

.sidebar-collapsed {
  width: 80px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1.25rem 0.5rem 4rem 0.5rem;
}

.menu-text {
  transition: all 0.3s ease;
  white-space: nowrap;
}

.menu-text-hidden {
  opacity: 0;
  width: 0;
  overflow: hidden;
}

.menu-icon {
  min-width: 2rem;
  width: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.2rem;
}

.menu-link {
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin: 0.25rem 0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  text-align: center;
}

.menu-link:hover {
  color: var(--hover-color) !important;
  width: 100%;
  text-align: left;
  justify-content: flex-start;
  align-items: flex-start;
  transition: all 0.2s ease;
}

.menu-link.active {
  color: var(--hover-color) !important;
  width: 100%;
  text-align: left;
  justify-content: flex-start;
  align-items: flex-start;
  transition: all 0.2s ease;
}

.toggle-button {
  position: absolute;
  right: 1rem;
  top: 1rem;
  width: 2rem;
  height: 2rem;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  z-index: 10;
  transition: all 0.3s ease;
}

@media (max-width: 425px) {
  .sidebar {
    width: 224px !important;
  }
  .toggle-button {
    display: none;
  }
  .menu-link.active {
    background-color: #0d9488 !important;
  }
}

@media (max-width: 768px) {
  .menu-text {
    opacity: 1 !important;
    width: auto !important;
  }
}

.toggle-button:hover {
  background: #f8f8f8;
  transform: scale(1.1);
}

.toggle-button i {
  color: #333;
  font-size: 0.8rem;
  transition: all 0.3s ease;
}
</style>

<template>
  <div :class="[isExpanded ? 'sidebar-expanded' : 'sidebar-collapsed', 'sidebar']">
    <!-- Toggle Button -->
    <div 
      @click="toggleSidebar"
      class="toggle-button text-right p-2"
    >
      <i :class="['fas', 'fa-bars']"></i>
    </div>

    <!-- Menu Items -->
    <div class="px-2 py-4">
      <template v-for="item in menuItems" :key="item.id">
        <Link 
          v-if="canUserAccess(item.roles)"
          class="menu-link" 
          :class="{ 'active': isUrl(item.route.substring(1)) }"
          :href="item.route"
          @mouseenter="hoveredItem = item.id"
          @mouseleave="hoveredItem = null"
          @click="handleMenuItemClick"
        >
          <div class="menu-icon">
            <template v-if="item.id === 'dashboard'">
              <i 
                :class="[
                  'fas',
                  item.icon,
                  'mr-2',
                  isUrl(item.route.substring(1)) ? 'text-white' : 'text-gray-400 group-hover:text-white menu-item'
                ]" 
                :style="{'--text-color': account.textSidebarColor ?? 'black', '--hover-color': account.textHoverSidebarColor}"
              />
            </template>
            <template v-else>
              <i 
                :class="[
                  'fas',
                  item.icon,
                  'mr-2',
                  isUrl(item.route.substring(1)) ? 'text-white' : 'text-gray-400 group-hover:text-white menu-item'
                ]" 
                :style="{'--text-color': account.textSidebarColor ?? 'black', '--hover-color': account.textHoverSidebarColor}"
              />
            </template>
          </div>

          <div 
            :class="[
              'menu-text ml-3',
              {'menu-text-hidden': !isExpanded},
              isUrl(item.route.substring(1)) ? 'menu-item font-bold' : 'text-gray-400 group-hover:text-white menu-item'
            ]" 
            :style="{'--text-color': account.textSidebarColor ?? 'black', '--hover-color': account.textHoverSidebarColor}"
          >
            {{ item.name }}
          </div>
        </Link>
      </template>
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'

export default {
  components: {
    Icon,
    Link,
  },
  props: {
    account: Object
  },
  created() {
    // En móviles siempre expandido, en desktop contraído
    const isMobile = window.innerWidth <= 425
    localStorage.setItem('sidebarExpanded', isMobile ? 'true' : 'false')
  },
  data() {
    return {
      role: this.$parent.auth.user.role,
      isExpanded: true,
      hoveredItem: null,
      menuItems: [
        { id: 'dashboard', name: 'Escritorio', icon: 'fa-home', route: '/', roles: ['*'] },
        { id: 'pos', name: 'Punto de Venta', icon: 'fa-cash-register', route: '/pos', roles: ['sales', 'admin'] },
        { id: 'clients', name: 'Clientes', icon: 'fa-address-book', route: '/clients', roles: ['admin', 'sales', 'financial'] },
        { id: 'products', name: 'Productos', icon: 'fa-boxes', route: '/products', roles: ['admin', 'endline', 'storer'] },
        { id: 'inventory', name: 'Inventario', icon: 'fa-warehouse', route: '/inventories', roles: ['admin', 'endline', 'storer'] },
        { id: 'orders', name: 'Ventas', icon: 'fa-chart-line', route: '/orders', roles: ['sales', 'admin', 'storer', 'financial'] },
        { id: 'cash-registers', name: 'Cajas', icon: 'fa-cash-register', route: '/cash-registers', roles: ['admin', 'financial'] },
        { id: 'cash-closures', name: 'Cierres de Caja', icon: 'fa-history', route: '/cash-closures', roles: ['admin', 'financial'] },
        //{ id: 'devolutions', name: 'Devoluciones', icon: 'fa-file-invoice-dollar', route: '/devolutions', roles: ['admin', 'financial'] },
        { id: 'shopping', name: 'Compras', icon: 'fa-truck', route: '/shoppings', roles: ['admin', 'financial'] },
        { id: 'providers', name: 'Proveedores', icon: 'fa-users', route: '/providers', roles: ['admin', 'financial'] },
        { id: 'spents', name: 'Gastos', icon: 'fa-file-invoice-dollar', route: '/spents', roles: ['admin', 'financial'] },
        { id: 'media', name: 'Multimedia', icon: 'fa-images', route: '/media', roles: ['admin'] },
        { id: 'reports', name: 'Reportes', icon: 'fa-file', route: '/reports', roles: ['admin'] }
      ]
    }
  },
  methods: {
    back() {
      this.$inertia.visit('/')
    },
    isUrl(...urls) {
      let currentUrl = this.$page.url.substr(1)
      if (urls[0] === '') {
        return currentUrl === ''
      }
      return urls.filter((url) => currentUrl.startsWith(url)).length
    },
    toggleSidebar() {
      this.isExpanded = !this.isExpanded
      localStorage.setItem('sidebarExpanded', this.isExpanded)
    },
    canUserAccess(roles) {
      return roles.includes('*') || roles.includes(this.role)
    },
    handleMenuItemClick() {
      if (window.innerWidth <= 425) {
        this.isExpanded = false
        localStorage.setItem('sidebarExpanded', 'false')
      }
    }
  },
  mounted() {
    // const savedState = localStorage.getItem('sidebarExpanded')
    // if (savedState !== null) {
    //   this.isExpanded = savedState === 'true'
    // }
  }
}
</script>
